import { useMemo } from 'react';
import { useConfigInputs, useMetrics, useVendorCtx } from 'shared/state/configContexts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import { toRackModels, type RackModel, type RackUnitModel } from 'shared/lib/rackLayout';
import { buildSlidePreviewModel } from './buildSlidePreviewModel';

// Fix round 1, ruling R11 (Critical): rackSlidePPTGenerator.js:173 —
// `const rackLayout = fullLayout.slice(0, 2);` — the exported deck only ever draws the
// first two racks. Previewing more than that shows a rep racks the customer will never
// see in the file.
const DECK_RACK_LIMIT = 2;

/** Fix round 1, ruling R10 (Critical): rackSlidePPTGenerator.js:145-148 drops two more
 *  things than toRackModels already omits (the 'empty' filler slots between a multi-U
 *  unit's first slot and the next):
 *    if (unit.type === 'empty' || unit.type === 'jbod-8u') return;
 *    const isJbod = unit.type === 'jbod';
 *    if (isJbod && !unit.isJbodOnly) return;
 *  `prefetchSvgs` (rackSlidePPTGenerator.js:107) excludes 'jbod-8u' the same way. Every
 *  jbod/jbod-8u RealisticRackVisualization.jsx emits today sets isJbodOnly: true
 *  (lines 83, 89), so the second clause is always true in practice — kept anyway so the
 *  preview tracks the generator's RULE, not today's data, if that ever changes.
 *
 *  Filtered here, in the feature, rather than in shared/lib/rackLayout.ts: Rack View
 *  legitimately draws every occupied slot — it's the system view, not the deck preview —
 *  so toRackModels and Task 3's tests must not change. */
function matchesDeckRule(unit: RackUnitModel): boolean {
  if (unit.type === 'jbod-8u') return false;
  if (unit.type === 'jbod' && !unit.isJbodOnly) return false;
  return true;
}

export function useSystemSlide() {
  const { config } = useConfigInputs();
  const metrics = useMetrics();
  const { vendor, productDb } = useVendorCtx();
  const { handleGenerateRackSlide } = useQuoteActionsCtx();

  const model = useMemo(() => buildSlidePreviewModel(config, metrics, vendor), [config, metrics, vendor]);

  const rackSize = Number(config.rackSize ?? 42);
  /** Controller ruling P3: reuse Task 3's shared reshaping of calculateRackLayout
   *  (shared/lib/rackLayout) instead of re-deriving the occupied-slot rule and the
   *  top-down mapping a second time. rackSlidePPTGenerator shares the same underlying
   *  calculateRackLayout, so this is also what the exported PPTX draws. */
  const allRacks = useMemo(() => toRackModels(config, productDb, rackSize), [config, productDb, rackSize]);

  const racks = useMemo<RackModel[]>(() => allRacks
    .slice(0, DECK_RACK_LIMIT)
    .map((rack) => ({ ...rack, units: rack.units.filter(({ unit }) => matchesDeckRule(unit)) })),
  [allRacks]);

  const totalRackCount = allRacks.length;
  const isTruncated = totalRackCount > DECK_RACK_LIMIT;

  // calculateRackLayout (inside toRackModels) reads the vendor off config.hardwareVendor,
  // not useVendorCtx().vendor.id (the same distinction rack-view's ruling P6 makes) —
  // RackImage needs to draw from that same source so the photo always matches the
  // layout it was resolved against, including the render or two during a vendor switch
  // where the two can transiently disagree. Confirmed correct in fix round 1 review
  // (matches RealisticRackVisualization.jsx:11, rackSlidePPTGenerator.js:166 and
  // useRackView.ts:51) — do not change this to vendor.id.
  const vendorId = config.hardwareVendor || 'AIC';

  // Fix round 1, ruling R14 (Minor): rackSlidePPTGenerator.js:168 sets
  // `showRack = rackSize !== 0` and suppresses the rack frame/rails for "No Rack", same
  // as Task 3's ruling R6 for Rack View. `rackSize` above (already passed into
  // toRackModels unchanged) is what drives this — 0 must keep reaching toRackModels as
  // 0, not 42, so calculateRackLayout's own 0→42 internal mapping is what keeps heights
  // and positions matching, not a value this hook substitutes.
  const showFrame = rackSize !== 0;

  return { model, racks, totalRackCount, isTruncated, onExport: handleGenerateRackSlide, vendorId, showFrame };
}
